import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Card, Container } from 'reactstrap';

import Meta from 'components/App/Meta';
import Loading from 'components/App/Loading';
import Breadcrumb from 'components/App/Breadcrumb';
import CustomerServiceHistoryTable from 'components/App/CustomerServiceHistoryTable';
import CustomerOverview from 'components/App/CustomerOverview';
import CustomerGiftTable from 'components/App/CustomerGiftTable';
import Pagination from 'components/Pagination';

import BookingPropTypes from 'propTypeModels/booking';
import { LOCATIONS } from 'constants/index';
import CustomerPropTypes from 'propTypeModels/customer';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import images from 'assets/images';
import styles from './CustomerDetails.module.scss';

const pageTitle = 'Chi tiết khách hàng';

const breadcrumbs = [
  {
    name: 'Danh sách khách hàng',
    link: LOCATIONS.CUSTOMERS,
  },
  {
    name: 'Chi tiết khách hàng',
    isActive: true,
  },
];

const MAIN_TAB = {
  OVERVIEW: 'OVERVIEW',
  BOOKINGS: 'BOOKINGS',
  GIFTS: 'GIFTS',
};

const MAIN_NAV_TABS = [
  { title: 'Tổng quan', value: MAIN_TAB.OVERVIEW },
  { title: 'Lịch sử dịch vụ', value: MAIN_TAB.BOOKINGS },
  { title: 'Lịch sử đổi quà', value: MAIN_TAB.GIFTS },
];

const DEFAULT_LIMIT = 10;

const CustomerDetails = ({
  customerDetails,
  customerBookings,
  totalCustomerBookings,
  getCustomerDetails,
  getCustomerBookings,
  customerDetailsLoading,
  customerBookingsLoading,
  blockCustomer,
  userGifts,
  getUserGiftsLoading,
  getUserGifts,
}) => {
  const { id } = useParams();
  const { search } = useLocation();
  const history = useHistory();

  const activeMainTab = React.useMemo(() => {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');
    if (tab && Object.values(MAIN_TAB).includes(tab)) {
      return tab;
    }
    return MAIN_TAB.OVERVIEW;
  }, [search]);

  const urlPage = React.useMemo(
    () => Number(new URLSearchParams(search).get('page') || 1),
    [search],
  );

  const totalPage = React.useMemo(
    () => Math.ceil(totalCustomerBookings / DEFAULT_LIMIT),
    [totalCustomerBookings],
  );

  const [isOpenModalBlockUser, setIsOpenModalBlockUser] = React.useState(false);

  const urlBookingStatus = React.useMemo(
    () => new URLSearchParams(search).get('bookingStatus') || 'ALL',
    [search],
  );

  const onChangeStatus = React.useCallback(
    (e) => {
      const params = new URLSearchParams(search);
      params.set('bookingStatus', e.target.value);
      params.set('page', '1');
      history.push({
        search: params.toString(),
      });
    },
    [history, search],
  );

  const onChangeMainTab = React.useCallback(
    (tab) => () => {
      const params = new URLSearchParams(search);
      params.set('tab', tab);
      params.set('page', '1');
      history.push({
        search: params.toString(),
      });
    },
    [history, search],
  );

  const onChangePage = React.useCallback(
    (selected) => {
      const params = new URLSearchParams(search);
      params.set('page', selected.selected + 1);
      history.push({
        search: params.toString(),
      });
    },
    [history, search],
  );

  const onClickCancel = React.useCallback(() => {}, []);

  const onClickBlock = React.useCallback(() => {
    setIsOpenModalBlockUser(true);
  }, []);

  const onCloseModalBlockUser = React.useCallback(() => {
    setIsOpenModalBlockUser(false);
  }, []);

  const onConfirmBlockUser = React.useCallback(() => {
    if (id) {
      blockCustomer({
        id,
        callback: () => {
          getCustomerDetails(id);
          setIsOpenModalBlockUser(false);
        },
      });
    }
  }, [id, blockCustomer, getCustomerDetails]);

  const blockCustomerMessage = React.useMemo(() => {
    const customerName = customerDetails?.fullName || customerDetails?.displayName || '';

    return customerName
      ? `Bạn có chắc chắn muốn chặn ${customerName} không?`
      : 'Bạn có chắc chắn muốn chặn người dùng này không?';
  }, [customerDetails?.displayName, customerDetails?.fullName]);

  React.useEffect(() => {
    getCustomerDetails(id);
    getCustomerBookings({
      id,
      page: activeMainTab === MAIN_TAB.BOOKINGS ? urlPage : 1,
      limit: DEFAULT_LIMIT,
      bookingStatus: urlBookingStatus !== 'ALL' ? urlBookingStatus : undefined,
    });
    getUserGifts({
      payload: {
        user: id,
        page: activeMainTab === MAIN_TAB.GIFTS ? urlPage : 1,
        limit: DEFAULT_LIMIT,
      },
    });
  }, [
    getCustomerBookings,
    getCustomerDetails,
    getUserGifts,
    id,
    urlPage,
    urlBookingStatus,
    activeMainTab,
  ]);

  return (
    <>
      <Meta name={pageTitle} />

      <div className={`page-content ${styles.Page}`}>
        <Container fluid>
          <Breadcrumb pageTitle={pageTitle} breadcrumbList={breadcrumbs} />

          <Loading
            loading={customerDetailsLoading || customerBookingsLoading}
            loaderOnPage
          />

          <Card className="Section p-0 mb-24">
            <div className="Tab__Header__Container">
              <div className="Tab__Header">
                <nav
                  className="Tab__Header__Nav"
                  aria-label="Menu quản lý chi tiết"
                >
                  {MAIN_NAV_TABS.map((tab) => (
                    <div key={tab.value} className="Tab__Header__Tab">
                      <button
                        type="button"
                        onClick={onChangeMainTab(tab.value)}
                        className={`Tab__Header__Tab__Item ${
                          activeMainTab === tab.value ? 'Active' : ''
                        }`}
                      >
                        {tab.title}
                      </button>
                    </div>
                  ))}
                </nav>
              </div>
            </div>
          </Card>

          {activeMainTab === MAIN_TAB.OVERVIEW && (
            <CustomerOverview
              customerDetails={customerDetails}
              onClickCancel={onClickCancel}
              onClickBlock={onClickBlock}
            />
          )}

          {activeMainTab === MAIN_TAB.BOOKINGS && (
            <>
              <Card className="Section" style={{ padding: '30px' }}>
                {/* <h5 className="Section__Title mb-20">Lịch sử dịch vụ</h5> */}
                <CustomerServiceHistoryTable
                  data={customerBookings}
                  loading={customerBookingsLoading}
                  bookingStatus={urlBookingStatus}
                  onChangeStatus={onChangeStatus}
                />
              </Card>

              {totalCustomerBookings > 0 && (
                <Pagination
                  page={urlPage}
                  pageCount={totalPage}
                  onChangePage={onChangePage}
                />
              )}
            </>
          )}

          {activeMainTab === MAIN_TAB.GIFTS && (
            <>
              <Card className="Section" style={{ padding: '30px' }}>
                {/* <h5 className="Section__Title mb-20">Lịch sử đổi quà</h5> */}
                <CustomerGiftTable
                  data={userGifts?.data || []}
                  loading={getUserGiftsLoading}
                />
              </Card>

              {Number(userGifts?.total || 0) > 0 && (
                <Pagination
                  page={urlPage}
                  pageCount={Math.ceil((userGifts?.total || 0) / DEFAULT_LIMIT)}
                  onChangePage={onChangePage}
                />
              )}
            </>
          )}

          <ModalRequestAction
            isOpen={isOpenModalBlockUser}
            icon={images.modalWarning.default}
            textRequest={blockCustomerMessage}
            onCloseModal={onCloseModalBlockUser}
            onConfirm={onConfirmBlockUser}
          />
        </Container>
      </div>
    </>
  );
};

CustomerDetails.propTypes = {
  customerDetails: CustomerPropTypes.customer,
  getCustomerDetails: PropTypes.func.isRequired,
  getCustomerBookings: PropTypes.func.isRequired,
  customerDetailsLoading: PropTypes.bool.isRequired,
  customerBookingsLoading: PropTypes.bool.isRequired,
  customerBookings: BookingPropTypes.bookings.isRequired,
  totalCustomerBookings: PropTypes.number.isRequired,
  blockCustomer: PropTypes.func.isRequired,
  userGifts: PropTypes.instanceOf(Object),
  getUserGiftsLoading: PropTypes.bool,
  getUserGifts: PropTypes.func.isRequired,
};

CustomerDetails.defaultProps = {
  customerDetails: null,
  userGifts: null,
  getUserGiftsLoading: false,
};

export default CustomerDetails;
